import { useParams, Link } from 'react-router-dom';
import { useLocalizedPath } from '@/hooks/useI18n';
import { useTranslation } from '@/hooks/useTranslation';
import { useEffect, useRef, useState } from 'react';
import { Container } from '@/components/Container';
import { NotFoundPage } from './NotFoundPage';
import { getPostBySlug, getAllPosts, type BlogPost } from '@/content/blog/loader';
import { SEO } from '@/components/SEO';
import { AuthorBox } from '@/components/AuthorBox';
import { defaultAuthor } from '@/data/author';
import { OptimizedImage } from '@/components/OptimizedImage';
import { useAnalytics } from '@/hooks/useAnalytics';
import {
  createBreadcrumbSchema,
  createFAQSchema,
  createCoreGraph,
  personId,
  ORG_ID,
  WEBSITE_ID,
  BUILD_DATE,
  SITE,
} from '@/lib/seo-utils';

interface TocItem {
  text: string;
  id: string;
}

export function BlogPostPage() {
  const localize = useLocalizedPath();
  const { t, lang } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const articleRef = useRef<HTMLElement>(null);
  const { trackOutbound } = useAnalytics();
  const [tocItems, setTocItems] = useState<TocItem[]>([]);
  const [activeId, setActiveId] = useState<string>('');

  const [post, setPost] = useState<BlogPost | undefined>(undefined);
  const [relatedPosts, setRelatedPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    if (!slug) {
      setLoading(false);
      return;
    }
    Promise.all([getPostBySlug(slug, lang), getAllPosts(lang)]).then(([foundPost, allPosts]) => {
      setPost(foundPost);
      // Regra de cluster: "Leia também" fica DENTRO do mesmo hub (não vaza para fora do cluster).
      // Posts sem hub (genéricos) mantêm o comportamento antigo.
      setRelatedPosts(
        foundPost?.hub
          ? allPosts.filter(p => p.slug !== slug && p.hub === foundPost.hub).slice(0, 3)
          : allPosts.filter(p => p.slug !== slug).slice(0, 3)
      );
      setLoading(false);
    });
  }, [slug, lang]);

  // Extrai TOC do DOM após renderização e adiciona IDs aos headings
  useEffect(() => {
    if (!articleRef.current) return;
    const headings = articleRef.current.querySelectorAll('h2');
    const items: TocItem[] = [];
    headings.forEach(h => {
      const text = h.textContent || '';
      const id = text
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      h.id = id;
      items.push({ text, id });
    });
    setTocItems(items);
  }, [post]);

  // Destaca item ativo no scroll
  useEffect(() => {
    if (tocItems.length === 0) return;
    const handleScroll = () => {
      const headings = articleRef.current?.querySelectorAll('h2');
      if (!headings) return;
      let current = '';
      headings.forEach(h => {
        const rect = h.getBoundingClientRect();
        if (rect.top <= 120) current = h.id;
      });
      setActiveId(current);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [tocItems]);

  if (loading) {
    return null;
  }

  if (!post) {
    return <NotFoundPage />;
  }

  const PostComponent = post.Component;
  const articleUrl = `${SITE.url}/blog/${post.slug}`;
  const articleImg = post.coverImage.startsWith('http')
    ? post.coverImage
    : `${SITE.url}${post.coverImage}`;
  const authorRef = { '@id': personId(defaultAuthor.slug) };

  return (
    <div>
      <SEO
        title={`${post.title} — ${t('blog.seoTitlePost')}`}
        description={post.excerpt}
        keywords={post.tags.join(', ')}
        ogImage={post.coverImage}
        ogType="article"
        canonical={`/blog/${post.slug}`}
        jsonLd={{
          '@context': 'https://schema.org',
          '@graph': [
            ...createCoreGraph(lang),
            {
              '@type': 'Article',
              '@id': `${articleUrl}#article`,
              headline: post.title,
              description: post.excerpt,
              url: articleUrl,
              image: articleImg,
              datePublished: post.publishedAt,
              dateModified: BUILD_DATE,
              author: authorRef,
              publisher: { '@id': ORG_ID },
              isPartOf: { '@id': WEBSITE_ID },
              mainEntityOfPage: { '@id': `${articleUrl}#webpage` },
              inLanguage: lang,
              ...(post.tags?.length && { keywords: post.tags.join(', ') }),
            },
            {
              '@type': 'WebPage',
              '@id': `${articleUrl}#webpage`,
              url: articleUrl,
              name: post.title,
              description: post.excerpt,
              isPartOf: { '@id': WEBSITE_ID },
              primaryImageOfPage: { '@type': 'ImageObject', url: articleImg },
              breadcrumb: { '@id': `${articleUrl}#breadcrumb` },
              author: authorRef,
              publisher: { '@id': ORG_ID },
              datePublished: post.publishedAt,
              dateModified: BUILD_DATE,
              inLanguage: lang,
            },
            createBreadcrumbSchema(
              [
                { name: t('seo.schema.guide.breadcrumbHome'), path: '/' },
                { name: 'Blog', path: '/blog' },
                { name: post.title, path: `/blog/${post.slug}` },
              ],
              `${articleUrl}#breadcrumb`
            ),
            ...(post.faq?.length
              ? [
                  {
                    ...createFAQSchema(post.faq),
                    '@id': `${articleUrl}#faq`,
                    author: authorRef,
                    publisher: { '@id': ORG_ID },
                  },
                ]
              : []),
          ],
        }}
      />

      {/* Hero do post */}
      <section className="relative h-[50vh] sm:h-[55vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <OptimizedImage
            src={post.coverImage}
            alt={post.title}
            className="absolute inset-0 w-full h-full object-cover"
            priority
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />
        </div>
        <Container className="relative z-10 pb-10 pt-32">
          <div className="max-w-3xl">
            <h1 className="font-editorial text-3xl sm:text-4xl md:text-5xl text-white mb-4 leading-tight tracking-tight">
              {post.title}
            </h1>
            <div className="flex items-center gap-4 font-body text-sm text-white/80">
              <span>{post.author}</span>
              <span>•</span>
              <span>{new Date(post.publishedAt).toLocaleDateString(lang)}</span>
              <span>•</span>
              <span>{t('blog.readingTime').replace('{{time}}', post.readingTime)}</span>
            </div>
          </div>
        </Container>
      </section>

      {/* Conteúdo + Sidebar TOC */}
      <section className="py-10 sm:py-14 bg-white">
        <Container className="max-w-6xl">
          <div className="grid lg:grid-cols-[1fr_280px] gap-10">
            {/* Artigo */}
            <article ref={articleRef} className="prose prose-lg max-w-none min-w-0">
              <PostComponent />

              {/* FAQ visível — necessário para rich results do Google */}
              {post.faq && post.faq.length > 0 && (
                <div className="mt-12 border-t border-sand-200 pt-10">
                  <h2 className="font-display text-2xl text-warm-dark mb-6">
                    Perguntas Frequentes
                  </h2>
                  <div className="space-y-4">
                    {post.faq.map((item, i) => (
                      <details
                        key={i}
                        className="group bg-sand-50 border border-sand-200 rounded-xl overflow-hidden"
                      >
                        <summary className="flex items-center justify-between cursor-pointer p-4 font-body font-medium text-warm-dark hover:bg-sand-100 transition-colors">
                          <span>{item.q}</span>
                          <span className="ml-4 text-coral text-lg leading-none group-open:rotate-180 transition-transform">
                            ▼
                          </span>
                        </summary>
                        <div className="px-4 pb-4 text-warm-dark/80 leading-relaxed">{item.a}</div>
                      </details>
                    ))}
                  </div>
                </div>
              )}

              {/* CTA final */}
              <div className="not-prose bg-ocean-50 border border-ocean-200 rounded-2xl p-6 sm:p-8 mt-12">
                <h3 className="font-display text-xl sm:text-2xl text-warm-dark mb-3">
                  {t('blog.ctaTitle')}
                </h3>
                <p className="text-warm-dark/80 mb-5 leading-relaxed">{t('blog.ctaDescription')}</p>
                <Link
                  to={localize('/')}
                  onClick={() =>
                    trackOutbound('/', 'CTA final do blog post', 'blog_cta')
                  }
                  className="inline-flex items-center gap-2 bg-coral hover:bg-coral-dark !text-white font-body font-semibold rounded-xl px-6 py-3 transition-all hover:-translate-y-0.5"
                >
                  {t('blog.ctaButton')}
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M14 5l7 7m0 0l-7 7m7-7H3"
                    />
                  </svg>
                </Link>
              </div>

              {/* Box de autoria — E-E-A-T */}
              <div className="not-prose">
                <AuthorBox author={defaultAuthor} />
              </div>
            </article>

            {/* Sidebar TOC */}
            {tocItems.length > 0 && (
              <aside className="hidden lg:block">
                <div className="sticky top-24 bg-sand-50 border border-sand-200 rounded-2xl p-5">
                  <h2 className="font-display text-sm uppercase tracking-wider text-warm-gray mb-4">
                    {t('blog.tocTitle')}
                  </h2>
                  <nav className="space-y-1" aria-label="Índice do artigo">
                    {tocItems.map(item => (
                      <a
                        key={item.id}
                        href={`#${item.id}`}
                        onClick={e => {
                          e.preventDefault();
                          document
                            .getElementById(item.id)
                            ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }}
                        className={`block font-body text-sm leading-snug py-1.5 border-l-2 pl-3 transition-colors ${
                          activeId === item.id
                            ? 'text-coral border-coral font-medium'
                            : 'text-warm-dark/60 hover:text-coral border-transparent hover:border-coral/40'
                        }`}
                      >
                        {item.text}
                      </a>
                    ))}
                  </nav>
                </div>
              </aside>
            )}
          </div>
        </Container>
      </section>

      {/* Posts relacionados */}
      {relatedPosts.length > 0 && (
        <section className="py-16 bg-sand-50 border-t border-sand-200">
          <Container className="max-w-4xl">
            <h2 className="font-display text-2xl text-warm-dark mb-8">{t('blog.relatedTitle')}</h2>
            <div className="grid md:grid-cols-3 gap-5">
              {relatedPosts.map(rel => (
                <Link
                  key={rel.slug}
                  to={localize(`/blog/${rel.slug}`)}
                  className="group block bg-white border border-sand-200 hover:border-teal-light/40 rounded-xl overflow-hidden transition-all hover:-translate-y-0.5 hover:shadow-md"
                >
                  <div className="h-40 overflow-hidden">
                    <img
                      src={rel.coverImage}
                      alt={rel.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-display text-warm-dark group-hover:text-teal-deep transition-colors tracking-wide">
                      {rel.title.toUpperCase()}
                    </h3>
                    <p className="font-body text-sm text-warm-gray mt-1 line-clamp-2">
                      {rel.excerpt}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </Container>
        </section>
      )}
    </div>
  );
}
